import type { AnyRealtimeRun } from "@trigger.dev/core/v3"
import { defaultRealtimeClient } from "./default-client"
import type {
	RealtimeStreamPart,
	RealtimeSubscriptionClient,
	RunListener,
	RunSubscriptionOptions,
	RunWithStreamsEvent,
	RunWithStreamsListener,
	RunWithStreamsSubscriptionOptions,
	TagSubscriptionOptions,
} from "./types"

interface SharedSubscription<TSnapshot, TListener> {
	refs: number
	abort: AbortController
	listeners: Set<TListener>
	errorListeners: Map<TListener, (error: Error) => void>
	snapshot: TSnapshot
}

interface RunSnapshot {
	run?: AnyRealtimeRun
}

interface TagSnapshot {
	runsById: Map<string, AnyRealtimeRun>
}

interface StreamSnapshot {
	run?: AnyRealtimeRun
	parts: RealtimeStreamPart[]
}

interface SubscriptionShape<TSnapshot, TListener, TEvent> {
	map: Map<string, SharedSubscription<TSnapshot, TListener>>
	key: string
	initSnapshot: () => TSnapshot
	source: (signal: AbortSignal) => AsyncIterable<TEvent>
	applyEvent: (
		snapshot: TSnapshot,
		event: TEvent,
		listeners: Set<TListener>,
	) => void
	replay: (snapshot: TSnapshot, listener: TListener) => void
}

const RECONNECT_DELAY_MS = 1_000

function subscriptionKey(parts: string[]): string {
	return parts.join(":")
}

export class RealtimeSubscriptionManager {
	constructor(
		private readonly client: RealtimeSubscriptionClient = defaultRealtimeClient,
	) {}

	private runSubscriptions = new Map<
		string,
		SharedSubscription<RunSnapshot, RunListener>
	>()
	private tagSubscriptions = new Map<
		string,
		SharedSubscription<TagSnapshot, RunListener>
	>()
	private streamSubscriptions = new Map<
		string,
		SharedSubscription<StreamSnapshot, RunWithStreamsListener>
	>()

	subscribeToRun(
		options: RunSubscriptionOptions,
		listener: RunListener,
	): () => void {
		const key = subscriptionKey([
			"run",
			options.runId,
			options.accessToken,
			options.baseURL ?? "",
		])

		return this.attach(
			{
				map: this.runSubscriptions,
				key,
				initSnapshot: (): RunSnapshot => ({}),
				source: (signal) =>
					this.client.subscribeToRun(options.runId, {
						accessToken: options.accessToken,
						baseURL: options.baseURL,
						signal,
					}),
				applyEvent: (snapshot, run, listeners) => {
					snapshot.run = run
					for (const l of listeners) {
						l(run)
					}
				},
				replay: (snapshot, l) => {
					if (snapshot.run) {
						l(snapshot.run)
					}
				},
			},
			listener,
			options.onError,
		)
	}

	subscribeToRunsWithTag(
		options: TagSubscriptionOptions,
		listener: RunListener,
	): () => void {
		const key = subscriptionKey([
			"tag",
			options.tag,
			options.accessToken,
			options.baseURL ?? "",
		])

		return this.attach(
			{
				map: this.tagSubscriptions,
				key,
				initSnapshot: (): TagSnapshot => ({ runsById: new Map() }),
				source: (signal) =>
					this.client.subscribeToRunsWithTag(options.tag, {
						accessToken: options.accessToken,
						baseURL: options.baseURL,
						signal,
					}),
				applyEvent: (snapshot, run, listeners) => {
					snapshot.runsById.set(run.id, run)
					for (const l of listeners) {
						l(run)
					}
				},
				replay: (snapshot, l) => {
					for (const run of snapshot.runsById.values()) {
						l(run)
					}
				},
			},
			listener,
			options.onError,
		)
	}

	subscribeToRunWithStreams<
		TStreams extends Record<string, unknown> = Record<string, unknown>,
	>(
		options: RunWithStreamsSubscriptionOptions,
		listener: RunWithStreamsListener<TStreams>,
	): () => void {
		const key = subscriptionKey([
			"stream",
			options.runId,
			options.accessToken,
			options.baseURL ?? "",
		])

		const erased = listener as RunWithStreamsListener

		return this.attach(
			{
				map: this.streamSubscriptions,
				key,
				initSnapshot: (): StreamSnapshot => ({ parts: [] }),
				source: (signal) =>
					this.client.subscribeToRunWithStreams(options.runId, {
						accessToken: options.accessToken,
						baseURL: options.baseURL,
						signal,
					}) as AsyncIterable<RunWithStreamsEvent>,
				applyEvent: (snapshot, event, listeners) => {
					if (event.type === "run") {
						snapshot.run = event.run
						for (const l of listeners) {
							l({ type: "run", run: event.run })
						}
						return
					}
					// Narrow the streams branch explicitly: with the default
					// `TStreams = Record<string, unknown>`, `keyof TStreams & string`
					// widens to `string`, so `event.type !== "run"` alone doesn't
					// eliminate the run-shape from the union. `"chunk" in event`
					// discriminates by property presence.
					if (!("chunk" in event)) {
						return
					}
					const part: RealtimeStreamPart = {
						key: event.type,
						chunk: event.chunk,
					}
					snapshot.parts.push(part)
					snapshot.run = event.run
					for (const l of listeners) {
						l({ type: "run", run: event.run })
						l({ type: "part", part })
					}
				},
				replay: (snapshot, l) => {
					if (snapshot.run) {
						l({ type: "run", run: snapshot.run })
					}
					for (const part of snapshot.parts) {
						l({ type: "part", part })
					}
				},
			},
			erased,
			options.onError,
		)
	}

	resetForTests() {
		for (const subscription of this.runSubscriptions.values()) {
			subscription.abort.abort()
		}
		for (const subscription of this.tagSubscriptions.values()) {
			subscription.abort.abort()
		}
		for (const subscription of this.streamSubscriptions.values()) {
			subscription.abort.abort()
		}
		this.runSubscriptions.clear()
		this.tagSubscriptions.clear()
		this.streamSubscriptions.clear()
	}

	private attach<TSnapshot, TListener, TEvent>(
		shape: SubscriptionShape<TSnapshot, TListener, TEvent>,
		listener: TListener,
		onError: (error: Error) => void,
	): () => void {
		let subscription = shape.map.get(shape.key)
		let created = false
		if (!subscription) {
			subscription = {
				refs: 0,
				abort: new AbortController(),
				listeners: new Set<TListener>(),
				errorListeners: new Map<TListener, (error: Error) => void>(),
				snapshot: shape.initSnapshot(),
			}
			shape.map.set(shape.key, subscription)
			created = true
		}

		subscription.refs += 1
		subscription.listeners.add(listener)
		subscription.errorListeners.set(listener, onError)
		if (created) {
			void this.consume(shape, subscription)
		}
		shape.replay(subscription.snapshot, listener)

		return () => {
			const current = shape.map.get(shape.key)
			if (!current || current !== subscription) {
				// Either already cleaned up, or the entry has been replaced by a
				// fresh subscription after we aborted — don't clobber it.
				return
			}
			current.listeners.delete(listener)
			current.errorListeners.delete(listener)
			current.refs -= 1
			if (current.refs <= 0) {
				current.abort.abort()
				if (shape.map.get(shape.key) === current) {
					shape.map.delete(shape.key)
				}
			}
		}
	}

	private async consume<TSnapshot, TListener, TEvent>(
		shape: SubscriptionShape<TSnapshot, TListener, TEvent>,
		subscription: SharedSubscription<TSnapshot, TListener>,
	) {
		const { signal } = subscription.abort

		try {
			while (!signal.aborted) {
				try {
					for await (const event of shape.source(signal)) {
						if (signal.aborted) {
							return
						}
						shape.applyEvent(
							subscription.snapshot,
							event,
							subscription.listeners,
						)
					}
					return
				} catch (error) {
					if (signal.aborted) {
						return
					}

					const subscriptionError =
						error instanceof Error ? error : new Error(String(error))
					for (const notifyError of subscription.errorListeners.values()) {
						notifyError(subscriptionError)
					}

					await waitForReconnect(signal)
				}
			}
		} finally {
			// Identity guard: an immediate resubscribe on the same key after we
			// aborted would have installed a fresh entry; only remove ours.
			if (shape.map.get(shape.key) === subscription) {
				shape.map.delete(shape.key)
			}
		}
	}
}

function waitForReconnect(signal: AbortSignal): Promise<void> {
	return new Promise((resolve) => {
		if (signal.aborted) {
			resolve()
			return
		}

		const timeout = setTimeout(finish, RECONNECT_DELAY_MS)
		signal.addEventListener("abort", finish, { once: true })

		function finish() {
			clearTimeout(timeout)
			signal.removeEventListener("abort", finish)
			resolve()
		}
	})
}

export const realtimeSubscriptionManager = new RealtimeSubscriptionManager()

export function createRealtimeSubscriptionManager(
	client: RealtimeSubscriptionClient,
) {
	return new RealtimeSubscriptionManager(client)
}
